import get from 'lodash.get';
import convertFractions from 'utils/convertFractions';

/**
 * Ingredient quantity tag
 * @param  {Object} recipeIngredientItem Ingredient object
 * @return {String}                      HTML tag for quantity
 */
function ingredientQtyTag(recipeIngredientItem) {
  const qty = get(recipeIngredientItem, 'fields.qty');
  return qty
    ? `<span class="ingredient__quantity">${convertFractions(qty)}</span>`
    : null;
}

/**
 * Title portion of ingredient markup, includes formatted quantity
 * and optional ReviewSet link
 * @param  {Object} recipeIngredientItem    Ingredient
 * @return {string}                         Formatted title
 */
function ingredientTitle(recipeIngredientItem) {
  const pluralIngredient = get(
    recipeIngredientItem,
    'fields.pluralIngredient',
    false,
  );
  const ingredient = get(recipeIngredientItem, 'fields.ingredient');
  const pluralTitle = get(ingredient, 'fields.pluralTitle', '');
  const title = get(ingredient, 'fields.title', '');
  return pluralIngredient ? pluralTitle : title;
}

/**
 * Full markup for an ingredient
 *   - <qty> <preText> <titleTag> <postText>
 * @param  {Object} recipeIngredientItem    Ingredient
 * @param  {String} recipeIngredientGroupId Contentful id
 * @return {String}                         Ingredient Markup
 */
function ingredientMarkup(recipeIngredientItem) {
  const qtyTag = ingredientQtyTag(recipeIngredientItem);
  const titleTag = ingredientTitle(recipeIngredientItem);
  let preText = get(recipeIngredientItem, 'fields.preText', '') || '';
  preText = preText.trim();

  let postText = get(recipeIngredientItem, 'fields.postText', '') || '';
  postText = postText.trim();

  if (postText.charAt(0) !== ',') postText = ` ${postText}`;
  return [qtyTag, ' ', preText, ' ', titleTag, postText].map((x) => x).join('');
}

function ingredientItemData(recipeIngredientItem, relatedDoc) {
  // for ingredients with a linked `document`, use the doc title
  let title = relatedDoc
    ? get(relatedDoc, 'fields.title')
    : ingredientTitle(recipeIngredientItem);

  let qty = get(recipeIngredientItem, 'fields.qty');
  qty = qty ? convertFractions(qty) : qty;

  let preText = get(recipeIngredientItem, 'fields.preText', '') || '';
  preText = preText.trim();

  let postText = get(recipeIngredientItem, 'fields.postText', '') || '';
  postText = postText.trim();
  // the title needs to contain the comma if postText starts with a comma
  // to avoid orphaned commas
  if (postText.charAt(0) === ',') {
    title = `${title},`;
    postText = postText.substring(1);
  }

  return {
    qty,
    preText,
    postText,
    title,
  };
}

const e = encodeURIComponent;
function createTagHref(categoryTitle, contentType, siteKey, tagTitle) {
  return `/search?q=${e(
    tagTitle,
  )}&dFR[search_site_list][0]=${siteKey}&fR[search_${e(
    categoryTitle,
  )}_list][0]=${e(tagTitle)}&fR[search_document_klass][0]=${e(contentType)}`;
}

function createTagEpisodeHref(season, siteKey) {
  return `/recipes/browse/season_${season}?q=&fR[search_browse_slugs][0]=season_${season}&fR[search_${siteKey}_episode_season][0]=${season}&fR[search_document_klass][0]=recipe&fR[search_site_list][0]=${siteKey}`;
}

/**
 * Select first book or book matching 'book' query string param
 * @param  {Object}  books -> list of books on recipe api payload
 * @param  {Object} queryParams -> url params from req
 * @return {Object} { title, url }
 */
function getBook({ book, books }, { referrer: objectID }) {
  if ((!books || books.length === 0) && !book) return null;

  // find the first book that's not discontinued
  const allBooks = [...books, book].filter((n) => n);
  let selectedBook =
    allBooks.find(({ isDiscontinued = false }) => isDiscontinued === false) ||
    allBooks[0];

  const browseEnabled = books && books.length > 0;
  // however, if there is a `referrer`, we use that no matter what
  if (typeof objectID !== 'undefined') {
    const bookId = objectID.replace('book_', '');
    selectedBook = books.find(({ id }) => id === bookId) || selectedBook;
  }
  const id = selectedBook.id;
  const metaData = selectedBook.fields
    ? selectedBook.fields.metaData
    : undefined;
  const buttonText = get(selectedBook, 'fields.buttonText');
  const cloudinaryId =
    get(selectedBook, 'fields.photo.public_id') ||
    get(metaData, 'fields.photo.public_id', null);
  const description =
    get(selectedBook, 'fields.description') ||
    get(metaData, 'fields.description', null);

  const currentPrice = get(selectedBook, 'fields.currentPrice');
  const documentCount = get(selectedBook, 'fields.documentCount');
  const isDiscontinued = get(selectedBook, 'fields.isDiscontinued', false);
  const previousPrice = get(selectedBook, 'fields.previousPrice');
  const slug = get(selectedBook, 'fields.slug');
  const title = get(selectedBook, 'fields.title');
  const url = get(selectedBook, 'fields.url');

  return {
    browseEnabled,
    buttonText,
    cloudinaryId,
    currentPrice,
    description,
    documentCount,
    id,
    isDiscontinued,
    previousPrice,
    slug,
    title,
    url,
  };
}

// in reverse order of preference
const tagMap = {
  equipment: ['main_ingredient', 'dish_type', 'recipe_type'],
  ingredient: ['main_ingredient', 'course', 'cuisine'],
};

// GLB-832 - these are the hard-coded books for episodes
const tvBooks = {
  atk: ' America’s Test Kitchen 20th Anniversary TV Show Cookbook',
  cco: 'The Complete Cook’s Country TV Show Season 11 Cookbook',
};

/**
 * Quick View panels include a book search result.
 * This method determines what query term we send
 * to Algolia. If there is an `episode`, use a
 * hard-coded book name based on site key.
 * If there is a `book` attrib, then use the book title
 * Otherwise, use tag values, key equipment and fall back to the title.
 * see algolia.getReviewableRelateds for query implementation
 *
 * @param  {doc}    recipe   Current Recipe
 * @param  {Object | null} book     Selected book attribution (from `book` or `books`)
 * @param  {String} siteKey  Current site key
 * @return {Array}           Book query terms to send to Algolia
 */
function getBookQueries(doc, book, siteKey, type) {
  const { episode, keyEquipment, tags, title } = doc;

  // default query used as a fallback
  const queries = { default: title };
  const showEpisode = type === 'episode' && episode;

  // episode quick view should always show the tvBooks selection
  if (showEpisode && tvBooks[siteKey]) {
    queries[type] = tvBooks[siteKey];
  }

  // cookbook recipes should always show the highlighted cookbook
  if (book && !showEpisode) {
    queries[type] = book.title;
  } else {
    // build a list of query terms matching the preferred tag types
    // for the current context (equipment or ingredient)
    if (tagMap[type] && tags && tags.length > 0) {
      queries[type] = tags.reduce(
        (accum, tag) => {
          if (tagMap[type].includes(get(tag, 'fields.categoryTitle'))) {
            accum.unshift(get(tag, 'fields.tagTitle'));
          }
          return accum;
        },
        [title],
      );
    }

    // for equipment quick view, use the names of the key equipment items
    // in addition to the tag data - adding them to the END of the list
    if (type === 'equipment' && Array.isArray(keyEquipment)) {
      queries[type] = keyEquipment.reduce((accum, equip) => {
        accum.push(get(equip, 'fields.title'));
        return accum;
      }, queries[type]);
    }
  }

  return queries;
}

const recipe = {
  createTagHref,
  createTagEpisodeHref,
  ingredientMarkup,
  getBook,
  getBookQueries,
  convertFractions,
  ingredientItemData,
};

export default recipe;
